import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import { authService } from '@/services/authService';
import { awsCredentialsService } from '@/services/awsCredentialsService';
import { getConfigValue } from '@/services/configService';
import { withPRM } from '@/utils/prmUtils';
import { requestReauth } from '@/services/session/reauth';

/**
 * The deployer-account Lambdas through which the portal reaches client and
 * NextGen accounts, and writes its own records. The browser holds no
 * credentials for client accounts (TASK-625) and no write rights in the
 * deployer account (TASK-630). Each request carries the person's portal
 * sign-in token, and the Lambda refuses it unless the token verifies and the
 * person is in the staff group (TASK-631, TASK-635).
 */
export type PortalLambda = 'NEXTGEN_BROKER_LAMBDA' | 'CLIENT_ACCESS_LAMBDA' | 'PORTAL_ADMIN_LAMBDA';

type LambdaReply<T> = { ok: true; result: T } | { ok: false; error?: string; code?: string };

/** A refusal from a portal Lambda, with its code ('reauth_required', 'forbidden', ...). */
export class PortalLambdaError extends Error {
  constructor(
    message: string,
    readonly code: string
  ) {
    super(message);
    this.name = 'PortalLambdaError';
  }
}

async function lambdaClient(region: string): Promise<LambdaClient> {
  // The deployer provider caches its credentials, so a page that makes many
  // calls does not ask the identity pool for each one (BUG-469). A call the
  // revoke refused is sent once more with new credentials (TASK-629 item 6).
  const credentials = await awsCredentialsService.getDeployerCredentials();
  return awsCredentialsService.withRevokedRetry(withPRM(LambdaClient, { region, credentials }));
}

async function idToken(): Promise<string> {
  const session = (await authService.ensureValidSession(60 * 1000)) || authService.getCurrentSession();
  if (!session) throw new Error('Sign in to the portal again.');
  return session.idToken;
}

/**
 * Call a portal Lambda. A destructive operation needs a sign-in from the last
 * hour (TASK-649): when the Lambda asks for one, the page asks the person for
 * their password and authenticator code, then sends the request once more.
 */
export async function invokePortalLambda<T>(lambda: PortalLambda, payload: Record<string, unknown>): Promise<T> {
  try {
    return await invokeOnce<T>(lambda, payload);
  } catch (error) {
    if (!(error instanceof PortalLambdaError) || error.code !== 'reauth_required') throw error;
    await requestReauth();
    return await invokeOnce<T>(lambda, payload);
  }
}

async function invokeOnce<T>(lambda: PortalLambda, payload: Record<string, unknown>): Promise<T> {
  const functionName = getConfigValue(lambda);
  if (!functionName) throw new Error(`The portal config has no ${lambda}. Reload the page.`);
  const region = getConfigValue('NEXTGEN_BROKER_REGION') || getConfigValue('AWS_REGION') || 'us-east-1';

  const client = await lambdaClient(region);
  const res = await client.send(
    new InvokeCommand({
      FunctionName: functionName,
      Payload: new TextEncoder().encode(JSON.stringify({ ...payload, idToken: await idToken() })),
    })
  );
  const body = res.Payload ? new TextDecoder().decode(res.Payload) : '';
  if (res.FunctionError) {
    let detail = '';
    try {
      detail = (JSON.parse(body) as { errorMessage?: string }).errorMessage ?? '';
    } catch {
      // The body is not JSON. The status line is enough.
    }
    throw new Error(`The portal service failed${detail ? `: ${detail}` : ''}. Try again.`);
  }
  if (!body) throw new Error('The portal service sent no answer. Try again.');
  const reply = JSON.parse(body) as LambdaReply<T>;
  if (!reply.ok) {
    throw new PortalLambdaError(reply.error || 'The portal service refused the request.', reply.code || 'error');
  }
  return reply.result;
}
